import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { IRosterOpsHost } from './roster-member-ops.service';
import { TeamManagementService } from './team-management.service';
import { RosterServiceService } from './roster-service.service';
import { RosterMemberService } from './roster-member.service';

/**
 * Extends IRosterOpsHost with properties needed for team CRUD and selection operations.
 */
export interface IRosterTeamOpsHost extends IRosterOpsHost {
  teamsMeta: any;
  teamName: any;
  team_name: any;
  slectedIndividualTeamArr: any[];
  selectedGroupProfileTeamsArr: any[];
  mockupReportArgs: any;
  mockupReportArgsArr: any[];
  selectAll: boolean;
  demo_roster_id: string;
  acme_roster: any;
  openCloseTeamPopup(): void;
  selectAllRoaster(checked: boolean): void;
  compareByName(a: any, b: any): number;
  rebuildAllTeamIdsTtl(): void;
  showError(error: any): void;
  removedMember(member: any, teamId?: string): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class RosterTeamOpsService {

  constructor(
    private teamManagementService: TeamManagementService,
    private rosterService: RosterServiceService,
    private rosterMemberService: RosterMemberService
  ) {}

  // ── add new team via API ───────────────────────────────────────────────────

  addTeam(host: IRosterTeamOpsHost): void {
    if (!host.teamName) return;
    let newTeamId: string;
    host.loading = true;
    this.teamManagementService.addTeam(host.id, host.teamName)
      .pipe(takeUntil(host.destroy$)).subscribe(
        (result) => {
          host.loading = false;
          newTeamId = result.team.id;
          if (host.modalSvc.createNewTeamBtn) { host.modalSvc.inviteTeamId = result.team.id; }
          host.teams.push(result['team']);
          if (host.teamsMeta) {
            host.teamsMeta = { ...host.teamsMeta, total_count: (host.teamsMeta.total_count || 0) + 1 };
          }
          host.teams.sort((a, b) => host.compareByName(a, b));
          host.teams.forEach((team) => {
            if (team.id == newTeamId && host.modalSvc.createNewTeamBtn) {
              team['checked'] = true;
            }
          });
          host.rebuildAllTeamIdsTtl();
          if (host.gridHirView === 'hierar') { host.hirerachicalData(); }
          host.selectedRosterMembers = [];
          host.modalSvc.showTeamModal = false;
          host.team_name = host.teamName;
        },
        (error) => {
          host.showError(error);
          host.loading = false;
          host.teamName = '';
        }
      );
  }

  // ── delete team via API ────────────────────────────────────────────────────

  deleteTeam(id: string, index: number, host: IRosterTeamOpsHost): void {
    this.teamManagementService.deleteTeam(host.id, id)
      .pipe(takeUntil(host.destroy$)).subscribe(
        () => {
          host.modalSvc.showRosterDelMem = false;
          host.teams.forEach((tm, ind) => {
            if (tm.id == id) {
              index = ind;
              tm.memberships.forEach((mbr) => {
                host.teamsData.members.forEach((mem, rInd) => {
                  if (mbr.member_id == mem.id) {
                    delete host.teamsData.members[rInd].teams_names[id];
                    host.teamsData.members[rInd].non_admin_unassgined_teams_count -= 1;
                  }
                });
              });
            }
          });
          host.teams.splice(index, 1);
          if (host.teamsMeta) {
            host.teamsMeta = { ...host.teamsMeta, total_count: Math.max(0, (host.teamsMeta.total_count || 0) - 1) };
          }
          host.rebuildAllTeamIdsTtl();
          if (host.gridHirView === 'hierar') { host.hirerachicalData(); }
          Swal.fire({ title: 'Nice!', text: 'The Team was deleted successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          host.modalSvc.showRosterDelMem = false;
          host.selectAll = false;
          host.selectAllRoaster(false);
        },
        (err) => {
          Swal.fire({ title: 'Sorry!', text: err, icon: 'error', timer: 2000, showConfirmButton: false });
        }
      );
  }

  // ── update team name via API ───────────────────────────────────────────────

  UpdateTeam(host: IRosterTeamOpsHost): void {
    if (!host.modalSvc.updatedTeamId) return;
    host.loading = true;
    this.teamManagementService.updateTeam(host.id, host.modalSvc.updatedTeamId, host.modalSvc.temp)
      .pipe(takeUntil(host.destroy$)).subscribe(
        () => {
          host.selectAll = false;
          host.selectAllRoaster(false);
          host.teams.sort((a, b) => host.compareByName(a, b));
          host.rebuildAllTeamIdsTtl();
          if (host.gridHirView == 'hierar') { host.hirerachicalData(); }
          host.loading = false;
          Swal.fire({ title: 'Nice!', text: 'The Team Name was updated successfully!', icon: 'success', timer: 2000, showConfirmButton: false });
          host.teams.forEach((team) => {
            if (team.id == host.modalSvc.updatedTeamId) { team.name = host.modalSvc.temp; }
          });
          host.openCloseTeamPopup();
        },
        (err) => {
          host.loading = false;
          Swal.fire({ title: 'Oops!', text: err?.error?.errors, icon: 'error', timer: 2000, showConfirmButton: false });
        }
      );
  }

  // ── bulk delete selected teams via API ────────────────────────────────────

  deleteSelectedTeams(host: IRosterTeamOpsHost): void {
    const teams = [...host.slectedIndividualTeamArr];
    const count = teams.length;
    if (!count) return;
    Swal.fire({
      title: `Delete ${count} team${count > 1 ? 's' : ''}?`,
      text: `This will permanently delete ${count} selected team${count > 1 ? 's' : ''}. This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (!result.isConfirmed) return;
      forkJoin(teams.map(team =>
        this.teamManagementService.deleteTeam(host.id, team.id).pipe(takeUntil(host.destroy$))
      )).subscribe(
        () => {
          teams.forEach(team => {
            const idx = host.teams.findIndex(t => t.id === team.id);
            if (idx !== -1) {
              host.teams[idx].memberships.forEach((mbr: any) => {
                host.teamsData?.members?.forEach((mem: any, rInd: number) => {
                  if (mbr.member_id === mem.id) {
                    delete host.teamsData.members[rInd].teams_names[team.id];
                    host.teamsData.members[rInd].non_admin_unassgined_teams_count -= 1;
                  }
                });
              });
              host.teams.splice(idx, 1);
            }
          });
          if (host.teamsMeta) {
            host.teamsMeta = { ...host.teamsMeta, total_count: Math.max(0, (host.teamsMeta.total_count || 0) - count) };
          }
          host.rebuildAllTeamIdsTtl();
          if (host.gridHirView === 'hierar') { host.hirerachicalData(); }
          host.selectedGroupProfileTeamsArr = [];
          host.slectedIndividualTeamArr = [];
          host.mockupReportArgsArr = [];
          Swal.fire({ title: 'Done!', text: `${count} team${count > 1 ? 's' : ''} deleted successfully.`, icon: 'success', timer: 2000, showConfirmButton: false });
        },
        (err) => {
          Swal.fire({ title: 'Sorry!', text: err, icon: 'error', timer: 2000, showConfirmButton: false });
        }
      );
    });
  }

  // ── promote member to team lead ───────────────────────────────────────────

  async mkTeamLead(member: any, role_id: any, teamData: any = undefined, host: IRosterTeamOpsHost): Promise<void> {
    if (host.demo_roster_id == host.id) {
      let updatedAcmeTeam;
      host.acme_roster.teams.forEach((team, tmInd) => {
        if (team.id == teamData.id) {
          team.memberships.forEach((teamMem, memInd) => {
            if (member.id == teamMem.id) {
              host.acme_roster.teams[tmInd].memberships[memInd].membership_role_id = role_id;
            }
          });
          updatedAcmeTeam = team;
        }
      });
      if (updatedAcmeTeam) {
        this.rosterService.mockupTeamReport(updatedAcmeTeam).then((args) => {
          host.mockupReportArgs = args;
        });
      }
    } else {
      this.rosterMemberService.updateMemberRole(member.identity_id, member.id, role_id)
        .pipe(takeUntil(host.destroy$)).subscribe((res) => {
          let updatedTeam;
          host.teams.forEach((team) => {
            if (team.id == member.team_id) {
              team.memberships.forEach((teamMem) => {
                if (member.id == teamMem.id) {
                  teamMem.membership_role_id = res['membership']?.membership_role_id;
                }
              });
              updatedTeam = team;
            }
          });
          if (updatedTeam) {
            this.rosterService.mockupTeamReport(updatedTeam).then((args) => {
              host.mockupReportArgs = args;
            });
          }
        });
      Swal.fire({ title: 'Nice!', text: "Member's role was updated successfully!", icon: 'success', timer: 2000, showConfirmButton: false });
    }
  }

  // ── toggle team selection for group profile / report ──────────────────────

  selectedGroupProfileTeams(team: any, host: IRosterTeamOpsHost): void {
    const isCurrentlySelected = host.slectedIndividualTeamArr.some(item => item.id === team.id);
    this.rosterService.setGraphData(team).then(res => {
      const idx = host.selectedGroupProfileTeamsArr.findIndex(item => item.id === res.id);
      if (idx !== -1) {
        host.selectedGroupProfileTeamsArr.splice(idx, 1);
        const teamIdx = host.slectedIndividualTeamArr.findIndex(item => item.id === team.id);
        if (teamIdx !== -1) { host.slectedIndividualTeamArr.splice(teamIdx, 1); }
      } else {
        host.selectedGroupProfileTeamsArr.push(res);
        host.slectedIndividualTeamArr.push(team);
      }
    });
    this.rosterService.mockupTeamReport(team).then(res => {
      host.mockupReportArgs = res;
      if (isCurrentlySelected) {
        const idx = host.mockupReportArgsArr.findIndex(item => item.id === team.id);
        if (idx !== -1) { host.mockupReportArgsArr.splice(idx, 1); }
      } else {
        host.mockupReportArgsArr.push(res);
      }
    });
  }

  // ── remove team member with Swal confirmation ─────────────────────────────

  async removeTeamMember(member: any, host: IRosterTeamOpsHost): Promise<void> {
    let f_name = 'No Name';
    if (member.first_name) {
      f_name = member?.first_name[0]?.toUpperCase() + member.first_name?.slice(1);
    }
    const team_name = member?.team_name[0]?.toUpperCase() + member.team_name?.slice(1);
    Swal.fire({
      title: 'Remove Member!',
      text: `Are you sure, you want to remove ${f_name} ${member?.last_name ? member.last_name : ''} from ${team_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DD6B55',
      cancelButtonColor: '##D0D0D0',
      confirmButtonText: 'Yes, Remove it!',
    }).then(async (result) => {
      if (result['value'] == true) {
        await host.removedMember(member);
        host.selectAll = false;
        host.selectAllRoaster(false);
      }
    });
  }
}
